import { auth } from './firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import authManager from '../utils/authManager';
import { loginRequest, msalInstance, silentRequest, getSilentRequestForAccount, getSsoSilentRequest, safeMsalLogin, safeMsalAcquireToken, safeMsalLoginRedirect, safeMsalSsoSilent, msalInteractionManager } from '../auth/msalConfig';
import { jwtDecode } from 'jwt-decode';
import { clearGraphClient } from './graphClient';

// Service gérant l'authentification des utilisateurs
class AuthService {
  constructor() {
    this.user = null;
    this.msalInstance = msalInstance;
    this.azureToken = null;

    // Circuit breaker pour éviter les boucles de refresh
    this.refreshAttempts = 0;
    this.lastRefreshAttemptTime = 0;
    this.maxRefreshAttemptsPerMinute = 5;
    this.refreshCooldownMs = 60 * 1000; // 1 minute

    // Écouter les changements d'état d'authentification
    onAuthStateChanged(auth, user => {
      this.user = user;
    });

    this.initializeAzureUser();
  }

  // Détecter si on est dans un contexte de popup
  isInPopupContext() {
    // Vérifier si la fenêtre a un opener (ouverte via window.open)
    const hasOpener = window.opener !== null && window.opener !== window;

    // Vérifier si on est dans une iframe
    const isInIframe = window.self !== window.top;

    return hasOpener || isInIframe;
  }

  // Circuit breaker : vérifier si on peut faire une tentative de refresh
  canAttemptRefresh() {
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastRefreshAttemptTime;

    // Si plus d'une minute s'est écoulée, réinitialiser le compteur
    if (timeSinceLastAttempt > this.refreshCooldownMs) {
      this.refreshAttempts = 0;
      this.lastRefreshAttemptTime = now;
      return true;
    }

    // Vérifier si on a dépassé le nombre maximum de tentatives
    if (this.refreshAttempts >= this.maxRefreshAttemptsPerMinute) {
      const remainingCooldown = Math.ceil((this.refreshCooldownMs - timeSinceLastAttempt) / 1000);
      return false;
    }

    return true;
  }

  // Enregistrer une tentative de refresh
  recordRefreshAttempt() {
    this.refreshAttempts++;
    this.lastRefreshAttemptTime = Date.now();
  }

  // Nettoyer sélectivement le cache MSAL corrompu sans tout supprimer
  cleanCorruptedMSALCache() {
    let cleanedCount = 0;

    // Nettoyer localStorage
    const localKeysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('msal')) {
        try {
          const value = localStorage.getItem(key);
          // Vérifier si la valeur peut être parsée (si c'est du JSON)
          if (value && value.startsWith('{')) {
            const parsed = JSON.parse(value);
            // Si c'est un token avec une expiration invalide, le marquer pour suppression
            if (parsed.exp && (typeof parsed.exp !== 'number' || parsed.exp <= 0)) {
              localKeysToRemove.push(key);
            }
          }
        } catch (e) {
          // Si on ne peut pas parser, c'est peut-être corrompu
          localKeysToRemove.push(key);
        }
      }
    }

    localKeysToRemove.forEach(key => {
      localStorage.removeItem(key);
      cleanedCount++;
    });

    // Nettoyer les flags de redirection en boucle dans sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('msal_redirect_count') || key.includes('msal_cleanup_done'))) {
        sessionKeysToRemove.push(key);
      }
    }

    sessionKeysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
      cleanedCount++;
    });

    return cleanedCount;
  }

  // Émettre une notification visuelle via événement personnalisé
  emitNotification(message, type = 'info', duration = 5000) {
    window.dispatchEvent(new CustomEvent('auth-notification', {
      detail: {
        message,
        type, // 'info', 'success', 'warning', 'error'
        duration,
        timestamp: Date.now()
      }
    }));
  }

  // Initialiser le client MSAL (Microsoft Authentication Library)
  initMsal(msalInstance) {
    this.msalInstance = msalInstance;
  }

  // Connexion avec Azure AD
  async signInWithAzure() {
    if (!this.msalInstance) {
      throw new Error('Client MSAL non initialisé');
    }

    try {
      // Utiliser le wrapper sécurisé pour éviter interaction_in_progress
      const loginResponse = await safeMsalLogin();

      // 🔥 AMÉLIORATION Safari : Sauvegarder le loginHint dans TOUS les endroits
      if (loginResponse.account && loginResponse.account.username) {
        const email = loginResponse.account.username;

        try {
          // localStorage (principal)
          localStorage.setItem('preferred_username', email);

          // sessionStorage (fallback Safari)
          sessionStorage.setItem('preferred_username', email);
        } catch (storageError) {
          console.warn('[AuthService] ⚠️ Erreur sauvegarde preferred_username:', storageError.message);
        }
      }

      // 🔥 FIX SAFARI : Stocker le token Azure avec les infos du compte
      // Ceci garantit que l'email et le nom sont correctement capturés dès le départ
      await this.updateAzureToken(loginResponse.accessToken, loginResponse.account);

      // Échanger le token Azure contre un token Firebase via notre gestionnaire
      const userData = await authManager.initialize(this.azureToken);

      return userData;
    } catch (error) {
      // Gestion spéciale des erreurs interaction_in_progress
      if (error.message && error.message.includes('interaction_in_progress')) {
        // Nettoyer l'état et attendre un peu
        await msalInteractionManager.clearInteractionState();
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Retry une seule fois
        try {
          const retryResponse = await safeMsalLogin();
          // 🔥 FIX SAFARI : Stocker le token avec les infos du compte
          await this.updateAzureToken(retryResponse.accessToken, retryResponse.account);
          const userData = await authManager.initialize(this.azureToken);
          return userData;
        } catch (retryError) {
          throw new Error('Erreur d\'authentification Azure. Veuillez réessayer dans quelques secondes.');
        }
      }

      throw error;
    }
  }

  // Déconnexion
  async signOut() {
    try {
      // Déconnexion de Firebase via le gestionnaire
      await authManager.signOut();
      
      // 🔒 CRITIQUE : Nettoyer le cache du client Microsoft Graph pour éviter 
      // qu'un utilisateur B utilise les données du compte A
      clearGraphClient();
      
      // Déconnexion d'Azure via redirect plutôt que popup
      if (this.msalInstance) {
        await this.msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
      }
      
      this.azureToken = null;
      this.user = null;
      
    } catch (error) {
      throw error;
    }
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    return !!this.user;
  }

  // Récupérer l'utilisateur actuel
  getCurrentUser() {
    return this.user;
  }

  // Récupérer l'ID de l'utilisateur actuel
  getCurrentUserId() {
    return this.user ? this.user.uid : null;
  }

  // Gérer une erreur d'authentification potentielle et réessayer l'opération
  async handleAuthError(error, operation, retryCallback) {
    if (await authManager.handleFirestoreError(error, operation)) {
      // Si le token a été rafraîchi, réessayer l'opération
      return retryCallback();
    }
    
    // Si ce n'est pas une erreur d'authentification ou si le rafraîchissement a échoué
    throw error;
  }

  // Récupérer le token Azure AD actuel avec le nouveau système de stockage
  getAzureToken() {
    // 1. Vérifier d'abord le cache en mémoire (plus rapide)
    if (this.azureToken) {
      return this.azureToken;
    }

    // 2. Vérifier le cache mémoire de fallback iOS
    if (this.azureUserMemoryCache && this.azureUserMemoryCache.azureAccessToken) {
      this.azureToken = this.azureUserMemoryCache.azureAccessToken;
      return this.azureToken;
    }

    // 3. Essayer de récupérer depuis le stockage approprié
    try {
      const azureUser = this.getAzureUserFromStorage();
      if (azureUser && azureUser.azureAccessToken) {

        // Vérifier l'âge du token si timestamp disponible
        if (azureUser.tokenTimestamp) {
          const tokenAgeHours = (Date.now() - azureUser.tokenTimestamp) / (1000 * 60 * 60);
          const maxAge = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 1 : 24; // 1h sur iOS, 24h sur desktop

          if (tokenAgeHours > maxAge) {
            // Log système au lieu de notification utilisateur
            import('./systemLogService.js').then(module => {
              const systemLogService = module.default;
              systemLogService.logToken(
                azureUser.uid || 'unknown',
                azureUser.email || 'unknown',
                azureUser.displayName || azureUser.email?.split('@')[0] || 'Utilisateur',
                `Token Azure expiré (âge: ${tokenAgeHours.toFixed(1)}h). Actualisation nécessaire.`,
                'warning',
                {
                  tokenAgeHours: tokenAgeHours.toFixed(1),
                  maxAgeHours: maxAge,
                  platform: /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'iOS' : 'desktop',
                  tokenType: 'azure'
                }
              ).catch(logError => {
                console.warn('[AuthService] Impossible de logger l\'expiration token:', logError);
              });
            }).catch(importError => {
              console.warn('[AuthService] Impossible d\'importer systemLogService:', importError);
            });
            return null;
          }
        }

        // Mettre en cache en mémoire pour les prochains accès
        this.azureToken = azureUser.azureAccessToken;
        return this.azureToken;
      }
    } catch (error) {
      console.error('[AuthService] ❌ Erreur lors de la récupération du token depuis le stockage:', error);
    }

    // 4. Fallback d'urgence : vérifier directement azureTestUser dans localStorage
    try {
      const legacyAzureUser = localStorage.getItem('azureTestUser');
      if (legacyAzureUser) {
        const azureUser = JSON.parse(legacyAzureUser);
        if (azureUser && azureUser.azureAccessToken) {
          this.azureToken = azureUser.azureAccessToken;

          // Migrer vers le nouveau système pour la prochaine fois
          this.saveAzureUserToStorage(azureUser);

          return this.azureToken;
        }
      }
    } catch (fallbackError) {
      console.error('[AuthService] ❌ Erreur fallback d\'urgence:', fallbackError);
    }

    return null;
  }

  // Vider le cache du token Azure pour forcer un renouvellement
  clearTokenCache() {
    this.azureToken = null;
    this.azureUserMemoryCache = null;

    // Nettoyer aussi le stockage pour être sûr
    this.clearAzureStorage();
  }

  // Méthode pour mettre à jour le token Azure dans tous les systèmes
  // azureAccount: Optional MSAL account object from login response (recommended for initial login)
  async updateAzureToken(newToken, azureAccount = null) {
    // 1. Mettre à jour le cache en mémoire
    this.azureToken = newToken;

    // 2. Récupérer les données utilisateur existantes
    let azureUser = this.getAzureUserFromStorage();

    // 3. Si aucune donnée n'existe, créer un objet avec les infos disponibles
    if (!azureUser || !azureUser.uid) {
      // 🔥 FIX SAFARI : Priorité 1 - Utiliser les infos du compte Azure (le plus fiable)
      if (azureAccount && azureAccount.username) {

        // Extraire le nom d'affichage depuis le compte Azure ou l'email
        const displayName = azureAccount.name ||
                          azureAccount.username?.split('@')[0] ||
                          'Utilisateur';

        azureUser = {
          email: azureAccount.username,
          displayName: displayName,
          azureAccountId: azureAccount.localAccountId || azureAccount.homeAccountId,
          azureAccessToken: newToken,
          tokenTimestamp: Date.now()
        };
      }
      // Priorité 2 : Essayer Firebase currentUser
      else {
        const currentUser = this.getCurrentUser();

        if (currentUser && currentUser.email && currentUser.email !== 'unknown@unknown.com') {

          azureUser = {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || currentUser.email.split('@')[0],
            azureAccessToken: newToken,
            tokenTimestamp: Date.now()
          };
        }
        // Priorité 3 : Utiliser le token JWT pour extraire des infos
        else {
          try {
            const decoded = jwtDecode(newToken);
            const emailFromToken = decoded.unique_name || decoded.upn || decoded.preferred_username;

            if (emailFromToken && emailFromToken !== 'unknown@unknown.com') {
              azureUser = {
                email: emailFromToken,
                displayName: decoded.name || emailFromToken.split('@')[0],
                azureAccessToken: newToken,
                tokenTimestamp: Date.now()
              };
            } else {
              // Dernier recours : objet minimal (mais on évite unknown@unknown.com)
              console.warn('[AuthService] ⚠️ Aucune source valide pour les infos utilisateur, création objet minimal');
              azureUser = {
                azureAccessToken: newToken,
                tokenTimestamp: Date.now()
              };
            }
          } catch (decodeError) {
            console.warn('[AuthService] ⚠️ Impossible de décoder le token pour extraire l\'email');
            azureUser = {
              azureAccessToken: newToken,
              tokenTimestamp: Date.now()
            };
          }
        }
      }
    } else {
      // 4. Mettre à jour le token et timestamp
      azureUser.azureAccessToken = newToken;
      azureUser.tokenTimestamp = Date.now();

      // 🔥 FIX SAFARI : Si on a un compte Azure et que l'email était manquant/invalide, le corriger
      if (azureAccount && azureAccount.username &&
          (!azureUser.email || azureUser.email === 'unknown@unknown.com')) {
        azureUser.email = azureAccount.username;
        azureUser.displayName = azureAccount.name || azureAccount.username.split('@')[0];
      }
    }

    // 5. Sauvegarder dans le stockage approprié
    this.saveAzureUserToStorage(azureUser);

    // 🔥 NOUVEAU : Sauvegarder le preferred_username pour Safari/SSO
    // Ceci est critique pour que tryRecoverAzureToken() fonctionne sur Safari
    if (azureUser && azureUser.email) {

      try {
        // localStorage (principal)
        localStorage.setItem('preferred_username', azureUser.email);

        // sessionStorage (fallback Safari)
        sessionStorage.setItem('preferred_username', azureUser.email);
      } catch (storageError) {
        console.warn('[AuthService] ⚠️ Erreur sauvegarde preferred_username:', storageError.message);
      }
    }

    // 🔥 CORRECTION CRITIQUE : Invalider le cache du client Microsoft Graph
    // Cela force la recréation du client avec le nouveau token
    clearGraphClient();

    // Log système au lieu de notification utilisateur
    import('./systemLogService.js').then(module => {
      const systemLogService = module.default;
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        systemLogService.logToken(
          currentUser.uid || 'unknown',
          currentUser.email || 'unknown',
          currentUser.displayName || currentUser.email?.split('@')[0] || 'Utilisateur',
          'Token Azure actualisé avec succès',
          'success',
          {
            refreshedAt: new Date().toISOString(),
            tokenType: 'azure'
          }
        ).catch(logError => {
          console.warn('[AuthService] Impossible de logger le refresh token:', logError);
        });
      }
    }).catch(importError => {
      console.warn('[AuthService] Impossible d\'importer systemLogService:', importError);
    });

    // Émettre un événement pour notifier les composants du refresh
    window.dispatchEvent(new CustomEvent('azure-token-refreshed', {
      detail: { timestamp: Date.now() }
    }));

  }

  // Méthode de retry avec backoff exponentiel pour le refresh du token Azure
  async refreshAzureTokenWithRetry(account, attempt = 0) {
    const maxAttempts = 3;
    const backoffMs = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s

    // Circuit breaker : vérifier si on peut tenter un refresh (uniquement à la première tentative)
    if (attempt === 0) {
      if (!this.canAttemptRefresh()) {
        throw new Error('Trop de tentatives de rafraîchissement du token. Veuillez patienter quelques instants et réessayer.');
      }
      // Enregistrer la tentative
      this.recordRefreshAttempt();
    }

    // Vérifier si on est dans un contexte de popup avant de tenter le refresh
    if (this.isInPopupContext()) {
      throw new Error('Impossible de rafraîchir le token dans un contexte de popup. Veuillez fermer cette fenêtre et réessayer.');
    }

    try {
      // Utiliser le helper qui crée une requête silencieuse optimisée avec le compte
      const silentRequestWithAccount = getSilentRequestForAccount(account);
      const response = await safeMsalAcquireToken(silentRequestWithAccount);

      // 🔥 FIX SAFARI : Passer les infos du compte lors du refresh
      await this.updateAzureToken(response.accessToken, response.account || account);
      return response.accessToken;

    } catch (error) {
      console.error(`[AuthService] ❌ Échec tentative ${attempt + 1}/${maxAttempts}:`, error.message);

      // Si block_nested_popups, ne pas réessayer
      if (error.message && error.message.includes('block_nested_popups')) {
        throw new Error('Contexte popup détecté. Veuillez fermer cette fenêtre et réessayer.');
      }

      // Si interaction_in_progress, nettoyer l'état
      if (error.message && error.message.includes('interaction_in_progress')) {
        await msalInteractionManager.clearInteractionState();
      }

      // Si on peut réessayer, attendre puis retry
      if (attempt < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return this.refreshAzureTokenWithRetry(account, attempt + 1);
      }

      // Si toutes les tentatives ont échoué, propager l'erreur
      throw error;
    }
  }

  // Récupérer un token Graph toujours valide (refresh auto si expiré)
  async getValidAzureToken() {
    // Auto-initialisation de MSAL si besoin
    if (!this.msalInstance && window.msal) {
      this.msalInstance = window.msal;
    }

    // 1. Vérifier si le token actuel est encore valide
    let token = this.getAzureToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);

        // Validation stricte : vérifier que exp existe et est un nombre valide
        if (!decoded.exp || typeof decoded.exp !== 'number' || decoded.exp <= 0) {
          this.azureToken = null;
          localStorage.removeItem('azureToken');
          // Continuer le processus de refresh ci-dessous
        } else {
          // Vérifier l'audience (audience pour Microsoft Graph doit être https://graph.microsoft.com)
          const hasCorrectAudience = decoded.aud === 'https://graph.microsoft.com' ||
                                   decoded.aud?.includes('00000003-0000-0000-c000-000000000000');

          if (!hasCorrectAudience) {
            // Token avec audience incorrecte, continuer vers refresh
          } else {
            // Vérifier l'expiration avec marge de 5 minutes pour rafraîchir de manière proactive
            const now = Date.now() / 1000;
            const expiryMargin = 5 * 60; // 5 minutes de marge

            if (decoded.exp > now + expiryMargin) {
              return token;
            }
          }
        }
      } catch (e) {
        console.warn('[AuthService] ⚠️ Erreur lors du décodage du token Azure, nettoyage...', e.message);
        this.azureToken = null;
        localStorage.removeItem('azureToken');
      }
    }

    // 2. Vérifier si MSAL est disponible et configuré
    if (!this.msalInstance) {
      // Log système au lieu de notification utilisateur
      try {
        const systemLogService = (await import('./systemLogService.js')).default;
        const currentUser = this.getCurrentUser();
        await systemLogService.logAuth(
          currentUser?.uid || 'unknown',
          currentUser?.email || 'unknown',
          currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Utilisateur',
          'Service d\'authentification Azure non disponible. Veuillez vous reconnecter.',
          'error',
          { 
            msalInstance: 'not_configured',
            tokenType: 'azure'
          }
        );
      } catch (logError) {
        console.warn('[AuthService] Impossible de logger l\'erreur MSAL:', logError);
      }
      throw new Error('Service d\'authentification Azure non disponible. Veuillez vous reconnecter.');
    }

    // Vérifier si des comptes sont disponibles
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('[AuthService] ⚠️ Aucun compte MSAL en mémoire, tentative de récupération du token...');

      // 🔥 AMÉLIORATION : Utiliser Firebase Auth directement au lieu de this.user
      // qui pourrait ne pas être synchronisé
      let currentUser = null;
      let isAzureUser = false;

      try {
        // Importer Firebase Auth dynamiquement
        const { auth } = await import('./firebase/config');
        currentUser = auth.currentUser;
        isAzureUser = currentUser?.uid?.startsWith('azure-');
      } catch (importError) {
        console.error('[AuthService] ❌ Erreur import Firebase Auth:', importError);
      }

      // 🔥 AMÉLIORATION : Toujours tenter la récupération, même si on ne peut pas vérifier l'utilisateur
      // Car l'utilisateur pourrait être connecté via Azure mais MSAL a perdu la session
      try {
        const recoveredToken = await this.tryRecoverAzureToken();
        if (recoveredToken) {
          return recoveredToken;
        }
      } catch (recoveryError) {
        console.warn('[AuthService] ⚠️ Échec de la récupération du token Azure:', recoveryError.message);
      }

      // Si la récupération a échoué et qu'on a confirmé que c'est un utilisateur Azure
      // Log système au lieu de notification utilisateur
      if (isAzureUser) {
        try {
          const systemLogService = (await import('./systemLogService.js')).default;
          await systemLogService.logAuth(
            currentUser?.uid || 'unknown',
            currentUser?.email || 'unknown',
            currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Utilisateur',
            'Session Azure expirée. Tentative de récupération échouée.',
            'warning',
            {
              accountsCount: 0,
              tokenType: 'azure',
              recoveryAttempted: true
            }
          );
        } catch (logError) {
          console.warn('[AuthService] Impossible de logger l\'échec de récupération:', logError);
        }
      }

      throw new Error('Aucun compte Azure connecté. Veuillez vous reconnecter.');
    }
    
    const account = accounts[0];

    // 3. Rafraîchir le token via MSAL avec protection interaction_in_progress
    
    // Détecter si on est sur mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

    try {
      // Utiliser la méthode de retry avec backoff exponentiel
      return await this.refreshAzureTokenWithRetry(account);

    } catch (silentError) {
      console.error('[AuthService] Échec du refresh silencieux après toutes les tentatives:', silentError.message);

      // Si le rafraîchissement silencieux échoue, essayer les méthodes interactives
      // mais seulement si on n'est pas dans un contexte automatique
      const isAutomaticRefresh = this.isAutomaticRefresh || false;

      if (isAutomaticRefresh) {
        throw new Error('Token Azure expiré. Veuillez actualiser la page ou vous reconnecter.');
      }

      // Sur mobile, éviter les popups et plutôt rediriger vers login
      if (isMobile) {
        try {
          // Vérifier qu'on n'est pas déjà en train de faire une redirection
          const redirectInProgress = localStorage.getItem('mobile_token_refresh_in_progress');
          if (!redirectInProgress) {
            localStorage.setItem('mobile_token_refresh_in_progress', 'true');
            localStorage.setItem('mobile_token_refresh_start_time', Date.now().toString());
            
            // Programmer le nettoyage du flag après 5 minutes
            setTimeout(() => {
              localStorage.removeItem('mobile_token_refresh_in_progress');
              localStorage.removeItem('mobile_token_refresh_start_time');
            }, 5 * 60 * 1000);

            // Utiliser le wrapper sécurisé pour la redirection
            await safeMsalLoginRedirect();
            
            // Note: le code après ne s'exécutera pas car la page sera redirigée
            throw new Error('Redirection vers l\'authentification Azure en cours...');
          } else {
            // Vérifier si le refresh est bloqué depuis trop longtemps
            const startTime = parseInt(localStorage.getItem('mobile_token_refresh_start_time') || '0');
            const elapsed = Date.now() - startTime;

            if (elapsed > 5 * 60 * 1000) { // Plus de 5 minutes
              localStorage.removeItem('mobile_token_refresh_in_progress');
              localStorage.removeItem('mobile_token_refresh_start_time');
              // Permettre un nouveau refresh
              return this.getValidAzureToken();
            } else {
              throw new Error('Authentification Azure en cours sur mobile. Veuillez patienter...');
            }
          }
        } catch (redirectError) {
          // Nettoyer les flags en cas d'erreur
          localStorage.removeItem('mobile_token_refresh_in_progress');
          localStorage.removeItem('mobile_token_refresh_start_time');
          
          throw new Error('Erreur lors de l\'authentification Azure mobile. Veuillez vous reconnecter.');
        }
      } else {
        // Sur desktop, essayer le popup avec protection
        try {
          const response = await safeMsalLogin();

          // 🔥 FIX SAFARI : Utiliser le nouveau système de mise à jour avec les infos du compte
          await this.updateAzureToken(response.accessToken, response.account);

          return response.accessToken;
        } catch (popupError) {
          // Si c'est encore une erreur interaction_in_progress, on abandonne temporairement
          if (popupError.message && popupError.message.includes('interaction_in_progress')) {
            // Déclencher l'événement pour l'UI mobile
            window.dispatchEvent(new CustomEvent('mobile-auth-error', {
              detail: {
                type: 'interaction_in_progress',
                message: 'Interaction d\'authentification en cours. Veuillez patienter ou redémarrer l\'application.',
                action: 'wait_or_restart'
              }
            }));
            throw new Error('Interaction d\'authentification en cours. Veuillez patienter ou actualiser la page.');
          }
          
          throw new Error('Erreur lors de l\'authentification Azure. Veuillez vous reconnecter.');
        }
      }
    }
  }

  // Marquer qu'on est dans un contexte de rafraîchissement automatique
  async getValidAzureTokenSilent() {
    this.isAutomaticRefresh = true;
    try {
      return await this.getValidAzureToken();
    } finally {
      this.isAutomaticRefresh = false;
    }
  }

  // Méthode spéciale pour mobile : éviter les redirections lors d'opérations critiques
  async getValidAzureTokenForEmailSending() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // Auto-initialisation de MSAL si besoin
    if (!this.msalInstance && window.msal) {
      this.msalInstance = window.msal;
    }

    // 1. Vérifier si le token actuel est encore valide (avec plus de tolérance sur iPhone)
    let token = this.getAzureToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);

        // Vérifier l'audience
        const hasCorrectAudience = decoded.aud === 'https://graph.microsoft.com' ||
                                 decoded.aud?.includes('00000003-0000-0000-c000-000000000000');

        if (hasCorrectAudience) {
          const now = Date.now() / 1000;
          // 🔥 AMÉLIORATION : Sur iPhone, être encore plus tolérant
          const margin = isIOS ? 10 : 60; // 10s sur iPhone vs 60s sur desktop

          if (decoded.exp && decoded.exp > now + margin) {
            return token;
          }
        }
      } catch (e) {
      }
    }

    // 2. Vérifier si MSAL est disponible
    if (!this.msalInstance) {
      throw new Error('Service d\'authentification Azure non disponible. Veuillez actualiser la page et vous reconnecter.');
    }

    const accounts = this.msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      throw new Error('Aucun compte Azure connecté. Veuillez vous reconnecter à votre compte Microsoft.');
    }

    const account = accounts[0];

    // 3. Strategy iPhone : Essayer plusieurs approches
    if (isIOS) {
      // Approche 1: Vérifier si on a un token récent dans localStorage
      const azureUserJson = localStorage.getItem('azureTestUser');
      if (azureUserJson) {
        try {
          const azureUser = JSON.parse(azureUserJson);
          if (azureUser.azureAccessToken && azureUser.tokenTimestamp) {
            const tokenAge = (Date.now() - azureUser.tokenTimestamp) / (1000 * 60); // en minutes

            // Si le token a moins de 45 minutes, l'utiliser (au lieu de 1h normalement)
            if (tokenAge < 45) {
              this.azureToken = azureUser.azureAccessToken;
              return azureUser.azureAccessToken;
            }
          }
        } catch (e) {

        }
      }

      // Approche 2: Fallback iPhone - Essayer le token existant même proche de l'expiration
      const existingToken = this.getAzureToken();
      if (existingToken) {
        try {
          const decoded = jwtDecode(existingToken);
          const now = Date.now() / 1000;
          const timeRemaining = decoded.exp - now;

          // Sur iPhone, si le token a expiré il y a moins de 5 minutes, l'essayer quand même
          // Car parfois les serveurs Microsoft Graph acceptent des tokens légèrement expirés
          if (timeRemaining > -300) { // -5 minutes en secondes
            this.azureToken = existingToken;
            return existingToken;
          }
        } catch (decodeError) {

        }
      }
    }
    
    // 4. Essayer le rafraîchissement silencieux avec retry
    const maxRetries = isIOS ? 2 : 1; // Plus de tentatives sur iPhone
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const tokenRequest = {
          ...loginRequest,
          account: account,
          forceRefresh: true
        };

        // Sur iPhone, ajouter des paramètres spéciaux
        if (isIOS) {
          tokenRequest.prompt = 'none'; // Forcer le mode silencieux
          tokenRequest.cacheLookupPolicy = 1; // Cache first
        }

        const response = await safeMsalAcquireToken(tokenRequest);

        // 🔥 FIX SAFARI : Utiliser le nouveau système de mise à jour avec les infos du compte
        await this.updateAzureToken(response.accessToken, response.account || account);

        return response.accessToken;

      } catch (silentError) {
        // Logging détaillé de l'erreur sur iPhone
        if (isIOS) {
          // Erreurs spécifiques iPhone
          if (silentError.message && silentError.message.includes('interaction_required')) {

            // Essayer le token existant même expiré comme ultime recours
            const fallbackToken = this.getAzureToken();
            if (fallbackToken && attempt === maxRetries) {
              this.azureToken = fallbackToken;
              return fallbackToken;
            }
          }
        }

        lastError = silentError;

        // Sur iPhone, attendre un peu avant de retry
        if (isIOS && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // 5. Toutes les tentatives ont échoué - Dernière stratégie de fallback iPhone
    if (isIOS) {
      // Ultime recours : Utiliser n'importe quel token existant sur iPhone
      const ultimateToken = this.getAzureToken();
      if (ultimateToken) {
        this.azureToken = ultimateToken;
        return ultimateToken;
      }

      // Essayer de récupérer un token depuis le localStorage même ancien
      const azureUserJson = localStorage.getItem('azureTestUser');
      if (azureUserJson) {
        try {
          const azureUser = JSON.parse(azureUserJson);
          if (azureUser.azureAccessToken) {
            this.azureToken = azureUser.azureAccessToken;
            return azureUser.azureAccessToken;
          }
        } catch (e) {

        }
      }

      throw new Error('Token Azure expiré sur iPhone. Actualisez la page (tirez vers le bas) et reconnectez-vous.');
    } else {
      throw new Error('Token Azure expiré. Veuillez actualiser la page ou vous reconnecter.');
    }
  }

  // Méthode stricte pour obtenir un token Azure valide - ne retourne JAMAIS un token expiré
  // Utilisée pour les opérations critiques comme l'envoi d'email où un token expiré est inutile
  async getValidAzureTokenStrict() {
    console.log('[AuthService] 🔒 getValidAzureTokenStrict() - Mode strict activé');

    // Auto-initialisation de MSAL si besoin
    if (!this.msalInstance && window.msal) {
      this.msalInstance = window.msal;
    }

    // 1. Vérifier si le token actuel est encore valide
    let token = this.getAzureToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;
        const expiryMargin = 5 * 60; // 5 minutes de marge

        // Validation stricte
        if (decoded.exp && typeof decoded.exp === 'number' && decoded.exp > now + expiryMargin) {
          const hasCorrectAudience = decoded.aud === 'https://graph.microsoft.com' ||
                                   decoded.aud?.includes('00000003-0000-0000-c000-000000000000');
          if (hasCorrectAudience) {
            console.log(`[AuthService] ✅ Token en cache valide (expire dans ${Math.floor((decoded.exp - now) / 60)} min)`);
            return token;
          }
        }
        console.log('[AuthService] ⚠️ Token en cache expiré ou invalide, rafraîchissement nécessaire...');
      } catch (e) {
        console.warn('[AuthService] ⚠️ Erreur décodage token:', e.message);
      }
    }

    // 2. Vérifier si MSAL est disponible
    if (!this.msalInstance) {
      throw new Error('Service d\'authentification Azure non disponible. Veuillez actualiser la page.');
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('[AuthService] ⚠️ Aucun compte MSAL, tentative de récupération SSO...');

      // Essayer la récupération SSO
      try {
        const recoveredToken = await this.tryRecoverAzureToken();
        if (recoveredToken) {
          // Vérifier que le token récupéré est vraiment valide
          const decoded = jwtDecode(recoveredToken);
          const now = Date.now() / 1000;
          if (decoded.exp && decoded.exp > now + 60) {
            console.log('[AuthService] ✅ Token récupéré via SSO et validé');
            return recoveredToken;
          }
          console.warn('[AuthService] ⚠️ Token SSO récupéré mais expiré');
        }
      } catch (recoveryError) {
        console.warn('[AuthService] ⚠️ Échec récupération SSO:', recoveryError.message);
      }

      throw new Error('Session Azure expirée. Veuillez actualiser la page pour vous reconnecter.');
    }

    const account = accounts[0];

    // 3. Essayer le rafraîchissement silencieux avec retry
    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[AuthService] 🔄 Tentative de refresh silencieux ${attempt}/${maxRetries}...`);

        const tokenRequest = {
          ...loginRequest,
          account: account,
          forceRefresh: true
        };

        const response = await safeMsalAcquireToken(tokenRequest);

        if (response && response.accessToken) {
          // Vérifier que le nouveau token est vraiment valide
          const decoded = jwtDecode(response.accessToken);
          const now = Date.now() / 1000;

          if (decoded.exp && decoded.exp > now + 60) {
            await this.updateAzureToken(response.accessToken, response.account || account);
            console.log('[AuthService] ✅ Nouveau token obtenu et validé');
            return response.accessToken;
          } else {
            console.warn('[AuthService] ⚠️ Token obtenu mais déjà expiré !');
          }
        }
      } catch (silentError) {
        console.warn(`[AuthService] ⚠️ Échec tentative ${attempt}:`, silentError.message);
        lastError = silentError;

        // Attendre avant de retry
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // 4. Si on arrive ici, impossible d'obtenir un token valide
    // NE PAS retourner un token expiré - throw une erreur claire
    console.error('[AuthService] ❌ Impossible d\'obtenir un token Azure valide après toutes les tentatives');
    throw new Error(
      'Impossible de rafraîchir votre session Azure. ' +
      'Veuillez actualiser la page pour continuer.'
    );
  }

  // Essayer de récupérer le token Azure via MSAL si manquant
  async tryRecoverAzureToken() {
    // 🔥 AMÉLIORATION : Vérifier d'abord le localStorage pour un token valide
    try {
      const azureUser = this.getAzureUserFromStorage();
      if (azureUser && azureUser.azureAccessToken) {
        // Vérifier si le token n'est pas expiré
        try {
          const decoded = jwtDecode(azureUser.azureAccessToken);
          const now = Date.now() / 1000;
          const margin = 60; // 1 minute de marge

          if (decoded.exp && decoded.exp > now + margin) {
            // Mettre à jour le cache en mémoire
            this.azureToken = azureUser.azureAccessToken;
            return azureUser.azureAccessToken;
          }
        } catch (decodeError) {
          console.warn('[AuthService] ⚠️ Impossible de décoder le token du storage');
        }
      }
    } catch (storageError) {
      console.warn('[AuthService] ⚠️ Erreur lecture du storage:', storageError.message);
    }

    // Auto-initialisation de MSAL si besoin
    if (!this.msalInstance && window.msal) {
      this.msalInstance = window.msal;
    }

    if (!this.msalInstance) {
      console.warn('[AuthService] ⚠️ MSAL non disponible');
      return null;
    }

    try {
      // Vérifier si MSAL a des comptes connectés
      const accounts = this.msalInstance.getAllAccounts();

      // Si des comptes MSAL existent, utiliser acquireTokenSilent
      if (accounts.length > 0) {
        const account = accounts[0];

        // Essayer un acquireTokenSilent
        const silentRequestWithAccount = getSilentRequestForAccount(account);
        const response = await safeMsalAcquireToken(silentRequestWithAccount);

        if (response && response.accessToken) {
          // 🔥 FIX SAFARI : Stocker le token avec les infos du compte
          await this.updateAzureToken(response.accessToken, response.account || account);

          return response.accessToken;
        }
      }

      // 🔥 AMÉLIORATION : Récupérer le loginHint depuis plusieurs sources (priorité)
      let loginHint = null;
      let loginHintSource = null;

      // Source 1 : localStorage.preferred_username (méthode principale)
      loginHint = localStorage.getItem('preferred_username');
      if (loginHint) {
        loginHintSource = 'localStorage.preferred_username';
      }

      // Source 2 : Email depuis azureTestUser (fallback Safari)
      if (!loginHint) {
        try {
          const azureUser = this.getAzureUserFromStorage();
          if (azureUser && azureUser.email) {
            loginHint = azureUser.email;
            loginHintSource = 'azureUser.email';
          }
        } catch (e) {
        }
      }

      // Source 3 : Firebase currentUser email (dernier fallback)
      if (!loginHint) {
        try {
          const { auth } = await import('./firebase/config');
          if (auth.currentUser && auth.currentUser.email) {
            loginHint = auth.currentUser.email;
            loginHintSource = 'Firebase.currentUser.email';
          }
        } catch (e) {
        }
      }

      if (loginHint) {
        // Créer la requête SSO silencieuse
        const ssoRequest = getSsoSilentRequest(loginHint);

        try {
          const ssoResponse = await safeMsalSsoSilent(ssoRequest);

          if (ssoResponse && ssoResponse.accessToken) {
            // 🔥 FIX SAFARI : Stocker le token avec les infos du compte Azure
            // Ceci est CRITIQUE pour Safari car c'est ici que l'email doit être capturé
            await this.updateAzureToken(ssoResponse.accessToken, ssoResponse.account);

            // 🔥 NOUVEAU : Sauvegarder le loginHint dans TOUS les endroits pour Safari
            if (ssoResponse.account?.username) {
              const email = ssoResponse.account.username;

              // localStorage
              localStorage.setItem('preferred_username', email);

              // sessionStorage (fallback Safari)
              try {
                sessionStorage.setItem('preferred_username', email);
              } catch (e) {
              }
            }

            return ssoResponse.accessToken;
          }
        } catch (ssoError) {
        }
      }

      return null;

    } catch (error) {
      console.error('[AuthService] ❌ Échec récupération token via MSAL:', error.message);

      // Si c'est une erreur qui nécessite une interaction, retourner null
      if (error.message && (error.message.includes('interaction_required') ||
                             error.message.includes('consent_required') ||
                             error.message.includes('login_required'))) {
        console.warn('[AuthService] ⚠️ Interaction utilisateur requise - Reconnexion nécessaire');
      }

      return null;
    }
  }

  // Initialiser les données utilisateur Azure depuis le stockage approprié
  initializeAzureUser() {
    try {
      const azureUser = this.getAzureUserFromStorage();
      if (azureUser && azureUser.azureAccessToken) {
        this.azureToken = azureUser.azureAccessToken;
      }
    } catch (error) {
      console.error('[AuthService] ❌ Erreur lors de l\'initialisation du token Azure:', error);
      this.clearAzureStorage();
    }
  }

  // Nouveau système de stockage hybride optimisé iOS
  getStorageForPlatform() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // Sur iOS/Safari, utiliser sessionStorage au lieu de localStorage
    if (isIOS || isSafari) {
      return {
        storage: sessionStorage,
        storageType: 'sessionStorage',
        keyPrefix: 'azure_session_'
      };
    } else {
      return {
        storage: localStorage,
        storageType: 'localStorage',
        keyPrefix: 'azure_local_'
      };
    }
  }

  // Méthode sécurisée pour récupérer les données utilisateur Azure
  getAzureUserFromStorage() {
    const { storage, keyPrefix, storageType } = this.getStorageForPlatform();

    try {
      // Essayer avec le nouveau système
      const azureUserJson = storage.getItem(keyPrefix + 'user');

      if (azureUserJson) {
        const azureUser = JSON.parse(azureUserJson);
        return azureUser;
      }

      // 🔥 FIX SAFARI : Essayer le backup storage sur Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        try {
          const backupStorage = storageType === 'localStorage' ? sessionStorage : localStorage;
          const backupPrefix = storageType === 'localStorage' ? 'azure_session_' : 'azure_local_';
          const backupJson = backupStorage.getItem(backupPrefix + 'user');

          if (backupJson) {
            const azureUser = JSON.parse(backupJson);

            // Restaurer dans le storage principal
            try {
              storage.setItem(keyPrefix + 'user', backupJson);
            } catch (e) {
            }

            return azureUser;
          }
        } catch (backupError) {
        }
      }

      // 🔥 FIX SAFARI : Vérifier le cache mémoire
      if (this.azureUserMemoryCache && this.azureUserMemoryCache.azureAccessToken) {
        return this.azureUserMemoryCache;
      }

      // Fallback vers l'ancien système (localStorage)
      const legacyData = localStorage.getItem('azureTestUser');

      if (legacyData) {
        const azureUser = JSON.parse(legacyData);

        // Migrer vers le nouveau système
        this.saveAzureUserToStorage(azureUser);

        // Nettoyer l'ancien système sur iOS
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          localStorage.removeItem('azureTestUser');
        }

        return azureUser;
      }

    } catch (error) {
      console.error('[AuthService] ❌ Erreur lors de la lecture du stockage Azure:', error);

      // 🔥 FIX SAFARI : Avant de tout nettoyer, essayer le cache mémoire
      if (this.azureUserMemoryCache && this.azureUserMemoryCache.azureAccessToken) {
        return this.azureUserMemoryCache;
      }

      // En cas d'erreur, essayer de nettoyer les données corrompues
      this.clearAzureStorage();
    }

    return null;
  }

  // 🔥 FIX SAFARI : Valider les données utilisateur avant sauvegarde
  validateAzureUserData(azureUser) {
    if (!azureUser) {
      console.warn('[AuthService] ⚠️ Tentative de sauvegarde d\'un objet azureUser vide');
      return false;
    }

    // Vérifier que l'email n'est pas invalide
    if (azureUser.email === 'unknown@unknown.com' || azureUser.email === '') {
      console.warn('[AuthService] ⚠️ Tentative de sauvegarde avec email invalide:', azureUser.email);
      // Ne pas rejeter complètement si on a au moins un token valide
      if (!azureUser.azureAccessToken) {
        return false;
      }
    }

    return true;
  }

  // Méthode sécurisée pour sauvegarder les données utilisateur Azure
  saveAzureUserToStorage(azureUser) {
    // 🔥 FIX SAFARI : Valider avant de sauvegarder
    if (!this.validateAzureUserData(azureUser)) {
      console.warn('[AuthService] ⚠️ Données azureUser invalides, sauvegarde annulée');
      return;
    }

    const { storage, keyPrefix, storageType } = this.getStorageForPlatform();

    try {
      // Ajouter un timestamp de sauvegarde
      const dataToSave = {
        ...azureUser,
        savedAt: Date.now(),
        storageType: storageType
      };

      const jsonData = JSON.stringify(dataToSave);
      storage.setItem(keyPrefix + 'user', jsonData);

      // 🔥 FIX SAFARI : Sauvegarder aussi en mémoire comme fallback
      this.azureUserMemoryCache = azureUser;

      // 🔥 FIX SAFARI : Sur Safari, sauvegarder aussi dans l'autre storage comme backup
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        try {
          const backupStorage = storageType === 'localStorage' ? sessionStorage : localStorage;
          const backupPrefix = storageType === 'localStorage' ? 'azure_session_' : 'azure_local_';
          backupStorage.setItem(backupPrefix + 'user', jsonData);
        } catch (backupError) {
        }
      }

    } catch (error) {
      console.error('[AuthService] ❌ Erreur sauvegarde storage:', error.message);

      // Sur iOS/Safari, si le storage échoue (quota exceeded), garder seulement en mémoire
      if (/iPad|iPhone|iPod/.test(navigator.userAgent) || /^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
        this.azureUserMemoryCache = azureUser;

        // Essayer de nettoyer l'ancien cache pour libérer de l'espace
        try {
          const oldKeys = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('msal') || key.includes('azure'))) {
              oldKeys.push(key);
            }
          }
          if (oldKeys.length > 10) {
            oldKeys.slice(0, 5).forEach(key => localStorage.removeItem(key));
            // Réessayer la sauvegarde
            storage.setItem(keyPrefix + 'user', JSON.stringify({
              ...azureUser,
              savedAt: Date.now(),
              storageType: storageType
            }));
          }
        } catch (cleanupError) {
        }
      }
    }
  }

  // Nettoyer tout le stockage Azure
  clearAzureStorage() {
    const { storage, keyPrefix, storageType } = this.getStorageForPlatform();

    try {

      // Nettoyer le nouveau système
      storage.removeItem(keyPrefix + 'user');

      // 🔥 FIX SAFARI : Nettoyer aussi le backup storage sur Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        try {
          const backupStorage = storageType === 'localStorage' ? sessionStorage : localStorage;
          const backupPrefix = storageType === 'localStorage' ? 'azure_session_' : 'azure_local_';
          backupStorage.removeItem(backupPrefix + 'user');
        } catch (backupError) {
        }
      }

      // Nettoyer l'ancien système
      localStorage.removeItem('azureTestUser');
      localStorage.removeItem('azureToken');
      localStorage.removeItem('azureTokenExpiry');

      // Nettoyer le cache mémoire
      this.azureToken = null;
      this.azureUserMemoryCache = null;

      // 🔒 CRITIQUE : Nettoyer le cache du client Microsoft Graph pour éviter
      // qu'un utilisateur B utilise les données du compte A
      clearGraphClient();

    } catch (error) {
      console.error('[AuthService] ❌ Erreur nettoyage stockage:', error.message);
    }
  }
}

const authService = new AuthService();
export default authService; 